'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/Input';
import { hasInvitePlaceholderName } from '../../../lib/invite-profile';
import { updateProfileNames } from '../api/auth-api';
import { AUTH_SESSION_QUERY_KEY } from '../constants';
import { writeStoredSession } from '../lib/authSessionStorage';
import { getApiErrorMessage } from '../hooks/useAuthMutations';
import {
  isAuthenticatedSession,
  useAuthSession,
} from '../hooks/useAuthSession';
import type { SignInResponse } from '../types/auth';

function mergeNamesIntoSession(
  session: SignInResponse,
  firstName: string,
  lastName: string,
): SignInResponse {
  return {
    ...session,
    data: {
      ...session.data,
      user: {
        ...session.data.user,
        firstName,
        lastName,
      },
    },
  };
}

export function InviteProfileCompletionGate() {
  const queryClient = useQueryClient();
  const { data: session } = useAuthSession();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});
  const [formError, setFormError] = useState<string | null>(null);

  const needsProfile =
    isAuthenticatedSession(session) &&
    hasInvitePlaceholderName(session.data.user);

  useEffect(() => {
    if (needsProfile) {
      setFirstName('');
      setLastName('');
      setFieldErrors({});
      setFormError(null);
    }
  }, [needsProfile, session?.data.user.userId]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticatedSession(session)) {
        throw new Error('Not signed in');
      }
      const token = session.data.accessToken;
      const fn = firstName.trim();
      const ln = lastName.trim();
      const nextErrors: typeof fieldErrors = {};
      if (!fn) nextErrors.firstName = 'First name is required';
      if (!ln) nextErrors.lastName = 'Last name is required';
      if (fn.length > 80) nextErrors.firstName = 'First name is too long';
      if (ln.length > 80) nextErrors.lastName = 'Last name is too long';
      if (
        fn.toLowerCase() === 'invited' &&
        ln.toLowerCase() === 'user'
      ) {
        setFormError('Please enter your real name, not the invitation placeholder.');
        throw new Error('__validation__');
      }
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        throw new Error('__validation__');
      }
      const res = await updateProfileNames(token, {
        firstName: fn,
        lastName: ln,
      });
      if (!res.success) {
        throw new Error(res.message ?? 'Update failed');
      }
      return { fn, ln };
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        AUTH_SESSION_QUERY_KEY,
        (prev: SignInResponse | null | undefined) => {
          if (!prev || !isAuthenticatedSession(prev)) return prev ?? null;
          return mergeNamesIntoSession(prev, result.fn, result.ln);
        },
      );
      const next = queryClient.getQueryData<SignInResponse | null>(
        AUTH_SESSION_QUERY_KEY,
      );
      if (next && isAuthenticatedSession(next)) {
        writeStoredSession(next);
      }
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    mutation.mutate(undefined, {
      onError: (err) => {
        if (err instanceof Error && err.message === '__validation__') return;
        setFormError(getApiErrorMessage(err));
      },
    });
  };

  if (!needsProfile) {
    return null;
  }

  return (
    <Dialog
      open
      onOpenChange={() => {}}
      preventDismiss
      aria-label='Complete your profile'
    >
      <DialogContent showCloseButton={false} className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Complete your profile</DialogTitle>
          <DialogDescription>
            Your account is ready. Enter your first and last name so they appear
            in the app header and profile.
          </DialogDescription>
        </DialogHeader>
        <form
          className='flex min-h-0 flex-1 flex-col'
          onSubmit={onSubmit}
        >
          <DialogBody className='space-y-4'>
          <div>
            <label
              className='mb-1 block text-sm font-medium text-foreground'
              htmlFor='invite-profile-first'
            >
              First name
            </label>
            <Input
              id='invite-profile-first'
              autoComplete='given-name'
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFieldErrors((x) => ({ ...x, firstName: undefined }));
              }}
              aria-invalid={Boolean(fieldErrors.firstName)}
            />
            {fieldErrors.firstName ? (
              <p className='mt-1 text-sm text-destructive' role='alert'>
                {fieldErrors.firstName}
              </p>
            ) : null}
          </div>
          <div>
            <label
              className='mb-1 block text-sm font-medium text-foreground'
              htmlFor='invite-profile-last'
            >
              Last name
            </label>
            <Input
              id='invite-profile-last'
              autoComplete='family-name'
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setFieldErrors((x) => ({ ...x, lastName: undefined }));
              }}
              aria-invalid={Boolean(fieldErrors.lastName)}
            />
            {fieldErrors.lastName ? (
              <p className='mt-1 text-sm text-destructive' role='alert'>
                {fieldErrors.lastName}
              </p>
            ) : null}
          </div>
          {formError ? (
            <p className='text-sm text-destructive' role='alert'>
              {formError}
            </p>
          ) : null}
          </DialogBody>
          <DialogFooter>
            <Button
              type='submit'
              className='w-full sm:w-auto'
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Save and continue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
